import { ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Button,
  Field,
  Heading,
  Input,
  Label,
  Paragraph,
  ValidationMessage,
} from '@digdir/designsystemet-react';
import type { ActionPlan, PlanArea } from '../../../api/workEnvironmentTypes';
import {
  useRegisterPlanReviewMutation,
  useRemovePlanReviewMutation,
} from '../../../hooks/mutations/useWorkEnvironmentAssessmentMutations';
import { longDate } from '../../Settings/privacyView';
import { FormErrorSummary, TextField } from '../components/FormParts';
import { reviewError } from '../followUpView';
import { dayOf } from '../planView';
import { dayAndMonth } from '../surveyProcessView';
import process from '../WorkEnvironmentSurveyProcess.module.css';
import classes from './FollowUp.module.css';

type Props = {
  surveyId: number;
  area: PlanArea;
  plan: ActionPlan;
  canFollowUp: boolean;
};

const DATE_FIELD = 'review-date';

export const ReviewsCard = ({
  surveyId,
  area,
  plan,
  canFollowUp,
}: Props): ReactElement => {
  const { t, i18n } = useTranslation();
  const today = dayOf();
  const [date, setDate] = useState(today);
  const [note, setNote] = useState('');
  const register = useRegisterPlanReviewMutation(surveyId);
  const remove = useRemovePlanReviewMutation(surveyId);
  const every = plan.frequency
    ? t(`workEnvironmentFollowUp.every.${plan.frequency}`)
    : '';
  const error = reviewError(register.error);
  const dateError =
    error === 'date' || error === 'already'
      ? t(
          `workEnvironmentFollowUp.reviews.${error === 'date' ? 'dateError' : 'already'}`,
        )
      : null;

  const submit = () =>
    register.mutate(
      { factorKey: area.key, date, note },
      {
        onSuccess: () => {
          setNote('');
          setDate(today);
        },
      },
    );

  return (
    <section
      aria-labelledby="follow-up-reviews-heading"
      className={process.card}
    >
      <div className={process.cardBody}>
        <div className={process.cardHeading}>
          <Heading level={2} data-size="sm" id="follow-up-reviews-heading">
            {t('workEnvironmentFollowUp.reviews.title')}
          </Heading>
          {plan.nextReview && (
            <Paragraph data-size="sm" className={process.subtle}>
              {t('workEnvironmentFollowUp.reviews.next', {
                date: dayAndMonth(plan.nextReview, i18n.language),
              })}
            </Paragraph>
          )}
        </div>
        <Paragraph data-size="sm">
          {t('workEnvironmentFollowUp.reviews.intro', { every })}
        </Paragraph>
        {plan.reviews.length > 0 && (
          <div className={process.sideHeading}>
            <Heading level={3} data-size="2xs">
              {t('workEnvironmentFollowUp.reviews.history')}
            </Heading>
            <ul className={classes.reviews}>
              {plan.reviews.map((review) => (
                <li key={review.on} className={classes.review}>
                  <span className={classes.reviewText}>
                    <span className="ds-paragraph" data-size="sm">
                      {review.by
                        ? t('workEnvironmentFollowUp.reviews.entryBy', {
                            date: longDate(review.on, i18n.language),
                            email: review.by,
                          })
                        : longDate(review.on, i18n.language)}
                    </span>
                    {review.note && (
                      <span
                        className={`ds-paragraph ${process.subtle}`}
                        data-size="sm"
                      >
                        {review.note}
                      </span>
                    )}
                  </span>
                  {canFollowUp && review.mine && (
                    <Button
                      variant="tertiary"
                      data-size="sm"
                      aria-label={t(
                        'workEnvironmentFollowUp.reviews.removeFor',
                        {
                          date: longDate(review.on, i18n.language),
                        },
                      )}
                      loading={
                        remove.isPending && remove.variables?.on === review.on
                      }
                      onClick={() =>
                        remove.mutate({ factorKey: area.key, on: review.on })
                      }
                    >
                      {t('workEnvironmentFollowUp.reviews.remove')}
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
      {canFollowUp && plan.approved && (
        <div className={`${process.cardFooter} ${classes.reviewForm}`}>
          <FormErrorSummary
            errors={
              dateError ? [{ fieldId: DATE_FIELD, message: dateError }] : []
            }
          />
          {(error === 'failed' || remove.isError) && (
            <Alert data-color="danger" data-size="sm">
              <Paragraph data-size="sm">
                {t('workEnvironmentFollowUp.reviews.failed')}
              </Paragraph>
            </Alert>
          )}
          <Field data-size="sm">
            <Label htmlFor={DATE_FIELD}>
              {t('workEnvironmentFollowUp.reviews.date')}
            </Label>
            <Input
              id={DATE_FIELD}
              type="date"
              max={today}
              value={date}
              aria-invalid={Boolean(dateError) || undefined}
              onChange={(event) => setDate(event.target.value)}
            />
            {dateError && <ValidationMessage>{dateError}</ValidationMessage>}
          </Field>
          <TextField
            id="review-note"
            label={t('workEnvironmentFollowUp.reviews.note')}
            value={note}
            onChange={setNote}
          />
          <div>
            <Button
              variant="secondary"
              loading={register.isPending}
              onClick={submit}
            >
              {t('workEnvironmentFollowUp.reviews.register')}
            </Button>
          </div>
          {register.isSuccess && (
            <Paragraph data-size="sm" role="status">
              {t('workEnvironmentFollowUp.reviews.registered')}
            </Paragraph>
          )}
        </div>
      )}
    </section>
  );
};
